import { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw,
  TrendingUp,
  Users,
  UserCheck,
  MousePointerClick,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Mail,
  CreditCard,
  Hourglass,
} from 'lucide-react';
import {
  SuperAdminService,
  type SignupFunnelAnalytics,
  type SignupSessionCard,
  type TrialAnalytics,
  type EngagementAnalytics,
  type EngagementBucket,
  type EngagementGroup,
} from '../../services/superAdminService';

const ENGAGEMENT_BUCKETS: { key: EngagementBucket; label: string; color: string }[] = [
  { key: 'daily', label: 'Engaged daily', color: 'bg-green-500' },
  { key: 'every3', label: 'Engaged every 3 days', color: 'bg-emerald-400' },
  { key: 'weekly', label: 'Engaged weekly', color: 'bg-amber-400' },
  { key: 'monthly', label: 'Engaged monthly', color: 'bg-orange-400' },
  { key: 'inactive', label: 'Not engaged', color: 'bg-gray-300' },
];

/** One column (trial or paid) of the engagement breakdown. */
function EngagementColumn({ title, group }: { title: string; group: EngagementGroup }) {
  const [openBucket, setOpenBucket] = useState<EngagementBucket | null>(null);
  const max = Math.max(1, ...ENGAGEMENT_BUCKETS.map((b) => group.buckets[b.key].length));

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/40 p-3.5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[13px] font-bold text-gray-800">{title}</h4>
        <span className="text-[11px] text-gray-400">{group.total} accounts</span>
      </div>
      <div className="space-y-2">
        {ENGAGEMENT_BUCKETS.map((bucket) => {
          const list = group.buckets[bucket.key];
          const isOpen = openBucket === bucket.key;
          return (
            <div key={bucket.key}>
              <button
                type="button"
                onClick={() => setOpenBucket(isOpen ? null : bucket.key)}
                className="w-full text-left"
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[12px] font-medium text-gray-700">{bucket.label}</span>
                  <span className="text-[12px] font-bold text-gray-900">{list.length}</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${bucket.color} transition-all`}
                    style={{ width: `${(list.length / max) * 100}%`, minWidth: list.length > 0 ? 6 : 0 }}
                  />
                </div>
              </button>
              {isOpen && list.length > 0 && (
                <div className="mt-1.5 mb-1 rounded-lg border border-gray-100 bg-white px-2.5 py-1.5 space-y-1">
                  {list.map((b) => (
                    <div key={b.businessId} className="flex items-center justify-between text-[12px]">
                      <span className="truncate text-gray-700">{b.name || 'Unnamed business'}</span>
                      <span className="shrink-0 ml-2 text-gray-400">
                        {b.activeDays} day{b.activeDays === 1 ? '' : 's'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const RANGES = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

const LOG_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Signed up' },
  { key: 'dropped', label: 'Dropped' },
  { key: 'active', label: 'In progress' },
] as const;
type LogFilter = (typeof LOG_FILTERS)[number]['key'];

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** One row in the rolling signup log. */
function SignupLogCard({ card }: { card: SignupSessionCard }) {
  const statusMeta =
    card.status === 'completed'
      ? { icon: CheckCircle2, text: 'Signed up', cls: 'bg-green-50 text-green-700 border-green-200' }
      : card.status === 'active'
        ? { icon: Clock, text: 'In progress', cls: 'bg-amber-50 text-amber-700 border-amber-200' }
        : { icon: XCircle, text: `Dropped — ${card.stageLabel}`, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  const StatusIcon = statusMeta.icon;
  const identity = card.businessName || card.email || 'Anonymous visitor';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-semibold text-gray-900 truncate">{identity}</span>
            {card.email && card.businessName && (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 truncate">
                <Mail className="h-3 w-3 shrink-0" />
                {card.email}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 flex-wrap text-[11px] text-gray-400">
            <span>{timeAgo(card.startedAt)}</span>
            <span className="capitalize rounded-full bg-gray-100 px-1.5 py-px font-medium text-gray-500">
              {card.flow}
            </span>
            {card.source !== 'direct' && <span className="truncate">{card.source}</span>}
            {card.planSelected && (
              <span className="rounded-full bg-indigo-50 px-1.5 py-px font-medium text-indigo-600">
                picked: {card.planSelected}
              </span>
            )}
          </div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusMeta.cls}`}
        >
          <StatusIcon className="h-3 w-3" />
          {statusMeta.text}
        </span>
      </div>

      {/* Journey progress */}
      <div className="mt-2.5">
        <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
          <span>
            {card.status === 'completed' ? 'Completed all steps' : `Reached: ${card.stageLabel}`}
          </span>
          <span>
            {card.stageIndex + 1}/{card.stages.length}
          </span>
        </div>
        <div className="flex gap-[3px]">
          {card.stages.map((label, i) => (
            <div
              key={label}
              title={label}
              className={`h-1.5 flex-1 rounded-full ${
                i <= card.stageIndex
                  ? card.status === 'completed'
                    ? 'bg-green-500'
                    : card.status === 'active'
                      ? 'bg-amber-400'
                      : 'bg-blue-400'
                  : 'bg-gray-100'
              }`}
            />
          ))}
        </div>
      </div>

      {card.errors.length > 0 && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-600">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span className="truncate">{card.errors.join(' · ')}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Super Admin → Signups: platform signup-funnel analytics.
 *
 * Renders traffic, the step-by-step funnel with drop-off, daily volume,
 * acquisition sources, and error reasons from signup_funnel_events. Built to
 * compare flow variants once the extended signup flow ships (the `flow`
 * dimension is already tracked and aggregated).
 */
export default function SignupAnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<SignupFunnelAnalytics | null>(null);
  const [log, setLog] = useState<SignupSessionCard[]>([]);
  const [logFilter, setLogFilter] = useState<LogFilter>('all');
  const [logShown, setLogShown] = useState(25);
  const [trials, setTrials] = useState<TrialAnalytics | null>(null);
  const [syncingTrials, setSyncingTrials] = useState(false);
  const [engagement, setEngagement] = useState<EngagementAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [analytics, sessionLog, trialData, engagementData] = await Promise.all([
        SuperAdminService.getSignupFunnelAnalytics(days),
        SuperAdminService.getSignupSessionLog(days, 500),
        SuperAdminService.getTrialAnalytics(),
        SuperAdminService.getEngagementAnalytics(30).catch((e) => {
          // RPC may not be applied yet — don't take down the whole tab.
          console.warn('Engagement analytics unavailable:', e);
          return null;
        }),
      ]);
      setData(analytics);
      setLog(sessionLog);
      setTrials(trialData);
      setEngagement(engagementData);
    } catch (err) {
      console.error('Failed to load signup analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load signup analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  /** Pull fresh subscription data from Stripe, then re-read trial metrics. */
  const syncTrialsFromStripe = useCallback(async () => {
    setSyncingTrials(true);
    try {
      await SuperAdminService.syncStripeBillingMirror({ syncAll: true });
      setTrials(await SuperAdminService.getTrialAnalytics());
    } catch (err) {
      console.error('Trial sync failed:', err);
      setError(err instanceof Error ? err.message : 'Trial sync failed');
    } finally {
      setSyncingTrials(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const maxDaily = Math.max(1, ...(data?.daily.map((d) => d.sessions) || [1]));

  return (
    <div className="space-y-4">
      {/* Header row: range picker + refresh */}
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={`rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                days === r.days ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[13px] font-semibold text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : data ? (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: MousePointerClick, label: 'Signup sessions', value: data.totals.sessions.toLocaleString() },
              { icon: Users, label: 'Unique visitors', value: data.totals.visitors.toLocaleString() },
              { icon: UserCheck, label: 'Accounts created', value: data.totals.completed.toLocaleString() },
              { icon: TrendingUp, label: 'Conversion', value: `${data.totals.conversionRate}%` },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Icon className="h-4 w-4" />
                    <span className="text-[11px] font-semibold uppercase tracking-wide">{card.label}</span>
                  </div>
                  <p className="mt-1.5 text-2xl font-bold text-gray-900">{card.value}</p>
                </div>
              );
            })}
          </div>

          {/* Funnel */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Signup funnel</h3>
            <p className="text-xs text-gray-400 mb-4">
              Unique sessions reaching each step — the red numbers are the drop-off from the previous step.
            </p>
            <div className="space-y-2.5">
              {data.funnel.map((stage) => (
                <div key={stage.step}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] font-medium text-gray-700">{stage.label}</span>
                    <span className="text-[12px] text-gray-500">
                      {stage.sessions.toLocaleString()} · {stage.pctOfTop}%
                      {stage.dropFromPrev > 0 && (
                        <span className="ml-2 text-red-500 font-semibold">−{stage.dropFromPrev.toLocaleString()}</span>
                      )}
                    </span>
                  </div>
                  <div className="h-5 rounded-md bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-md bg-gradient-to-r from-blue-500 to-indigo-500 transition-all"
                      style={{ width: `${Math.max(stage.pctOfTop, stage.sessions > 0 ? 2 : 0)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Daily volume */}
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Daily traffic</h3>
              <p className="text-xs text-gray-400 mb-4">Sessions started per day (green = accounts created)</p>
              <div className="flex items-end gap-[3px] h-32">
                {data.daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col justify-end gap-[2px] group relative">
                    <div
                      className="w-full rounded-sm bg-blue-200"
                      style={{ height: `${(d.sessions / maxDaily) * 100}%`, minHeight: d.sessions > 0 ? 3 : 0 }}
                    />
                    <div
                      className="w-full rounded-sm bg-green-500"
                      style={{ height: `${(d.completed / maxDaily) * 100}%`, minHeight: d.completed > 0 ? 3 : 0 }}
                    />
                    <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] text-white z-10">
                      {d.date}: {d.sessions} sessions · {d.completed} signups
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sources */}
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Where signups come from</h3>
              <p className="text-xs text-gray-400 mb-4">By referral partner, UTM source, or referrer domain</p>
              {data.sources.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No traffic in this period</p>
              ) : (
                <div className="space-y-1.5">
                  {data.sources.map((s) => (
                    <div key={s.source} className="flex items-center gap-3 text-[13px]">
                      <span className="flex-1 min-w-0 truncate font-medium text-gray-700">{s.source}</span>
                      <span className="text-gray-500 whitespace-nowrap">{s.sessions.toLocaleString()} sessions</span>
                      <span className="w-20 text-right font-semibold text-green-600 whitespace-nowrap">
                        {s.completed} signup{s.completed === 1 ? '' : 's'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Flow variants (ready for the extended flow) */}
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Flow variants</h3>
              <p className="text-xs text-gray-400 mb-4">
                Standard vs. extended signup — comparison lights up once the extended flow ships.
              </p>
              <div className="space-y-2">
                {data.flows.map((f) => (
                  <div key={f.flow} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2.5">
                    <span className="text-[13px] font-semibold text-gray-800 capitalize">{f.flow}</span>
                    <span className="text-[12px] text-gray-500">
                      {f.sessions.toLocaleString()} sessions ·{' '}
                      <span className="font-semibold text-green-600">{f.completed} signups</span>
                      {f.sessions > 0 && (
                        <span className="ml-1 text-gray-400">({Math.round((f.completed / f.sessions) * 100)}%)</span>
                      )}
                    </span>
                  </div>
                ))}
                {data.flows.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No sessions yet</p>
                )}
              </div>
            </div>

            {/* Errors */}
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Friction & errors</h3>
              <p className="text-xs text-gray-400 mb-4">What's stopping people mid-signup</p>
              {data.errors.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No errors recorded</p>
              ) : (
                <div className="space-y-1.5">
                  {data.errors.map((e) => (
                    <div key={e.reason} className="flex items-center gap-2 text-[13px]">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      <span className="flex-1 font-medium text-gray-700">{e.reason.replace(/_/g, ' ')}</span>
                      <span className="text-gray-500">{e.count.toLocaleString()}×</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Trials */}
          {trials && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <h3 className="text-sm font-bold text-gray-900">Free trials</h3>
                <button
                  type="button"
                  onClick={() => void syncTrialsFromStripe()}
                  disabled={syncingTrials}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[12px] font-semibold text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${syncingTrials ? 'animate-spin' : ''}`} />
                  Sync from Stripe
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                Cardless trials on platform plans — who's trialing now, who added a card, and how
                trials convert to paid.
              </p>

              {trials.needsSync && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
                  Trial data hasn't been synced from Stripe yet — hit "Sync from Stripe" to backfill.
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                {[
                  { icon: Hourglass, label: 'Active trials', value: trials.totals.activeTrials.toLocaleString() },
                  { icon: CreditCard, label: 'Added a card', value: trials.totals.activeWithCard.toLocaleString() },
                  { icon: UserCheck, label: 'Converted to paid', value: trials.totals.converted.toLocaleString() },
                  { icon: XCircle, label: 'Lost after trial', value: trials.totals.lost.toLocaleString() },
                  { icon: TrendingUp, label: 'Conversion', value: `${trials.totals.conversionRate}%` },
                ].map((card) => {
                  const Icon = card.icon;
                  return (
                    <div key={card.label} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                      <div className="flex items-center gap-1.5 text-gray-400">
                        <Icon className="h-3.5 w-3.5" />
                        <span className="text-[10px] font-semibold uppercase tracking-wide">{card.label}</span>
                      </div>
                      <p className="mt-1 text-xl font-bold text-gray-900">{card.value}</p>
                    </div>
                  );
                })}
              </div>

              {trials.activeTrialList.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No businesses currently in a trial</p>
              ) : (
                <div className="space-y-1.5">
                  {trials.activeTrialList.map((t, i) => (
                    <div
                      key={t.businessId ?? i}
                      className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2 text-[13px]"
                    >
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-gray-900 truncate block">
                          {t.businessName || t.email || 'Unknown business'}
                        </span>
                        <span className="text-[11px] text-gray-400 truncate block">
                          {[t.planLabel, t.amountMonthly ? `$${t.amountMonthly}/mo` : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                      {t.hasCard ? (
                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                          <CreditCard className="h-3 w-3" />
                          Card added
                        </span>
                      ) : (
                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                          No card yet
                        </span>
                      )}
                      <span
                        className={`shrink-0 w-24 text-right text-[12px] font-semibold ${
                          (t.daysLeft ?? 99) <= 2 ? 'text-red-600' : (t.daysLeft ?? 99) <= 5 ? 'text-amber-600' : 'text-gray-600'
                        }`}
                      >
                        {t.daysLeft !== null ? `${t.daysLeft}d left` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Engagement */}
          {engagement && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-bold text-gray-900 mb-1">Engagement</h3>
              <p className="text-xs text-gray-400 mb-4">
                How often each account is used (any owner or team-member activity counts), over the
                last {engagement.windowDays} days. Every account falls into exactly one bucket —
                click a bucket to see who's in it.
              </p>
              <div className="grid lg:grid-cols-2 gap-4">
                <EngagementColumn title="Trial accounts" group={engagement.trial} />
                <EngagementColumn title="Paid subscriptions" group={engagement.paid} />
              </div>
            </div>
          )}

          {/* Rolling signup log */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <h3 className="text-sm font-bold text-gray-900">Signup log</h3>
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                {LOG_FILTERS.map((f) => {
                  const count =
                    f.key === 'all' ? log.length : log.filter((c) => c.status === f.key).length;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => {
                        setLogFilter(f.key);
                        setLogShown(25);
                      }}
                      className={`rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${
                        logFilter === f.key
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {f.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              Every signup attempt as it happens — each card shows how far the visitor got before
              stopping, or that they finished. Emails appear once the visitor enters one.
            </p>
            {(() => {
              const filtered = logFilter === 'all' ? log : log.filter((c) => c.status === logFilter);
              if (filtered.length === 0) {
                return <p className="text-sm text-gray-400 text-center py-8">No signup sessions in this period</p>;
              }
              return (
                <>
                  <div className="grid gap-2.5 lg:grid-cols-2">
                    {filtered.slice(0, logShown).map((card) => (
                      <SignupLogCard key={card.sessionId} card={card} />
                    ))}
                  </div>
                  {filtered.length > logShown && (
                    <div className="mt-3 text-center">
                      <button
                        type="button"
                        onClick={() => setLogShown((n) => n + 50)}
                        className="rounded-lg border border-gray-200 bg-white px-4 py-1.5 text-[13px] font-semibold text-gray-600 hover:text-gray-900"
                      >
                        Show more ({filtered.length - logShown} remaining)
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </>
      ) : null}
    </div>
  );
}
